import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { getPath } from 'utils/Router';
import { sidebar } from '../../config.js';
import { slide as Menu } from 'react-burger-menu';
import Locale from '../Locale';
import { useI18n } from 'utils/I18n/context';
// storage HOC is injected by container; no direct import here

const images = {
    logo: require('img/main-logo.svg'),
    logoExtended: require('img/main-logo-exteneded.svg'),
    cross: require('img/cross.svg'),
    home: require('img/icons/home.svg'),
    offers: require('img/icons/offers.svg'),
    statistics: require('img/icons/statistics.svg'),
    finance: require('img/icons/finance.svg'),
    instruments: require('img/icons/instruments.svg'),
    information: require('img/icons/information.svg'),
    arrowNav: require('img/icons/arrow_nav.svg'),
};

function SideBar({
                     list,
                     activeItemIndex: initialIndex,
                     openSubmenu: initialOpenSubmenu,
                     isNavExtended,
                     toggleNav,
                     location,
                     isOpen,
                     setStorage,
                 }) {
    const { t } = useI18n();
    const staticNames = t?.sidebar || {};

    const [activeItemIndex, setActiveItemIndex] = useState(initialIndex);
    const [openSubmenu] = useState(Boolean(initialOpenSubmenu));

    const safeList = useMemo(() => (Array.isArray(list) ? list : []), [list]);

    const isMatchPath = useCallback(
        (data) => {
            const params = {
                exact: false,
                href: getPath(data.key),
                ...data,
            };
            const currentPath = location;
            if (params.exact) return currentPath === params.href;
            return !!currentPath.match(params.regexp || params.href);
        },
        [location]
    );

    const getActiveIndex = useCallback(
        () => safeList.findIndex((item) => isMatchPath(item)),
        [safeList, isMatchPath]
    );

    useEffect(() => {
        setActiveItemIndex(getActiveIndex());
    }, [location, safeList, getActiveIndex]);

    const onCloseMenu = useCallback(() => {
        setStorage({ isOpen: false });
    }, [setStorage]);

    const onClickItem = useCallback(
        (event) => {
            const elem = event.target.closest('.nav__tab');
            if (!elem) return;
            const index = +elem.getAttribute('data-index');
            setActiveItemIndex(index);
            if (elem.getAttribute('data-link')) onCloseMenu();
        },
        [onCloseMenu]
    );

    const onToggleNav = useCallback(() => {
        toggleNav(!isNavExtended);
    }, [toggleNav, isNavExtended]);

    const getSubMenuClass = useCallback(
        (item) => (isMatchPath(item) ? 'nav__sub-menu--active' : ''),
        [isMatchPath]
    );

    const getGroupClassName = useCallback(
        (active) =>
            classNames({
                active,
                opened: active && openSubmenu,
            }),
        [openSubmenu]
    );

    return (
        <Menu
            pageWrapId="page-wrapper"
            outerContainerId="wrapper"
            isOpen={!!isOpen}
            onStateChange={({ isOpen: open }) => setStorage({ isOpen: open })}
            customBurgerIcon={false}
            disableAutoFocus
        >
            <div className="sidebar__logo_wrapper">
                <div className="sidebar__logo_wrapper__inner">
                    <Link
                        className="sidebar__logo"
                        to={getPath('dashboard')}
                        data-link={true}
                        onClick={onCloseMenu}
                    >
                        <img src={images[isNavExtended ? 'logoExtended' : 'logo']} alt="" />
                    </Link>
                    <div onClick={onCloseMenu} className="sidebar__close">
                        <img src={images.cross} alt="" />
                    </div>
                </div>
            </div>

            <nav className="nav">
                <ul>
                    {safeList.map((item, i) => {
                        const isActive = i === activeItemIndex;
                        return (
                            <li className={getGroupClassName(isActive)} key={i}>
                                {item.isNativeLink ? (
                                    <a className="nav__tab has-sub" href={item.href} target={item.target}>
                    <span className="nav-label">
                      {staticNames[item.nameKey || item.key] ?? (item.nameKey || item.key)}
                    </span>
                                    </a>
                                ) : (
                                    <Link
                                        className="nav__tab has-sub"
                                        to={getPath(item.key)}
                                        data-index={i}
                                        data-link={true}
                                        onClick={onClickItem}
                                    >
                                        {item.icon && <img src={images[item.icon]} alt="" />}
                                        {isNavExtended ? (
                                            <span className="nav-label bold">
                        {staticNames[item.nameKey || item.key] ?? (item.nameKey || item.key)}
                      </span>
                                        ) : null}
                                    </Link>
                                )}
                                {item.subMenu && isNavExtended ? (
                                    <ul className="nav__sub-menu">
                                        {item.subMenu.map((sub) => (
                                            <li key={sub.key} className={getSubMenuClass(sub)}>
                                                <Link
                                                    className="nav__tab"
                                                    to={getPath(sub.key)}
                                                    data-index={i}
                                                    data-link={true}
                                                    onClick={onClickItem}
                                                >
                          <span className="nav-label">
                            {staticNames[sub.nameKey || sub.key] ?? (sub.nameKey || sub.key)}
                          </span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                ) : null}
                            </li>
                        );
                    })}
                    <li className="nav__toggle">
                        <button className="nav__tab" onClick={onToggleNav}>
                            <img className={isNavExtended ? '' : 'reverse'} src={images.arrowNav} alt="" />
                            {isNavExtended ? staticNames.collapse : null}
                        </button>
                    </li>
                </ul>
            </nav>

            <Locale />
        </Menu>
    );
}

SideBar.propTypes = {
    list: PropTypes.array,
    activeItemIndex: PropTypes.number,
    openSubmenu: PropTypes.bool,
    isNavExtended: PropTypes.bool,
    toggleNav: PropTypes.func.isRequired,
    location: PropTypes.string,
    isOpen: PropTypes.bool,
    setStorage: PropTypes.func.isRequired,
};

SideBar.defaultProps = {
    list: sidebar,
    activeItemIndex: 0,
    openSubmenu: true,
    isOpen: false,
};

export default SideBar;
